import {
  CHUNK_INTERVAL_MS,
  FrameType,
  HEARTBEAT_MS,
  encodeFrame,
  type ServerMessage,
} from "@protocol";
import {
  deleteChunk,
  getChunk,
  patchSession,
  pendingSeqs,
  type RecorderSession,
} from "./chunkStore";

/**
 * Where the bytes come from. A live capture drains IndexedDB; a dropped file
 * is read slice by slice straight from the `File`, with no local copy, because
 * the file itself is already the durable original.
 */
export interface ChunkSource {
  /** Every seq not yet acked, in order - including those in flight. */
  pendingSeqs(): Promise<number[]>;
  read(seq: number): Promise<{ payload: Uint8Array; clientTs: number } | null>;
  /** The server has it on disk; forget it. */
  acked(seq: number): Promise<void>;
  /** The resume handshake: what the server already holds from earlier sockets. */
  resumed?(lastAckedSeq: number, missingSeqs: number[]): void;
}

function indexedDbSource(recordingId: string): ChunkSource {
  return {
    pendingSeqs: () => pendingSeqs(recordingId),
    read: async (seq) => {
      const chunk = await getChunk(recordingId, seq);
      if (chunk === null) return null;
      return {
        payload: new Uint8Array(await chunk.blob.arrayBuffer()),
        clientTs: chunk.clientTs,
      };
    },
    acked: (seq) => deleteChunk(recordingId, seq),
  };
}

/**
 * A file cut into fixed-size slices, seq = slice index. The pending set lives
 * in memory: after a reload the recovery banner asks for the file again, and
 * the server's resume handshake prunes whatever already arrived.
 */
export class FileChunkSource implements ChunkSource {
  readonly chunkCount: number;
  private readonly pending: Set<number>;

  constructor(
    private readonly file: Blob,
    readonly chunkBytes: number,
  ) {
    this.chunkCount = Math.max(1, Math.ceil(file.size / chunkBytes));
    this.pending = new Set(Array.from({ length: this.chunkCount }, (_, seq) => seq));
  }

  async pendingSeqs(): Promise<number[]> {
    return [...this.pending].sort((a, b) => a - b);
  }

  async read(seq: number): Promise<{ payload: Uint8Array; clientTs: number } | null> {
    if (!this.pending.has(seq)) return null;
    const start = seq * this.chunkBytes;
    const slice = this.file.slice(start, Math.min(start + this.chunkBytes, this.file.size));
    return { payload: new Uint8Array(await slice.arrayBuffer()), clientTs: Date.now() };
  }

  async acked(seq: number): Promise<void> {
    this.pending.delete(seq);
  }

  resumed(lastAckedSeq: number, missingSeqs: number[]): void {
    const missing = new Set(missingSeqs);
    for (let seq = 0; seq <= lastAckedSeq; seq++) {
      if (!missing.has(seq)) this.pending.delete(seq);
    }
  }
}

/**
 * Drains IndexedDB over the ingest WebSocket, PLAN.md §7 steps 5 and "Reconnect".
 *
 * The uploader owns no audio state of its own - the chunk store is the queue.
 * Everything here is about getting bytes across an unreliable link:
 *
 *  - a chunk is deleted only when its ack arrives, so a dropped socket
 *    re-sends rather than loses;
 *  - reconnects back off 1s → 30s with jitter, indefinitely, because a lecture
 *    hall can be offline for the whole hour;
 *  - on reconnect the server's `resume` says what it already holds, so only the
 *    real remainder goes over the wire again.
 */

const BACKOFF_MIN_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;
/** How long after the last chunk a stopped recording gives up waiting. */
const FINALIZE_GRACE_MS = CHUNK_INTERVAL_MS * 2;

/**
 * File slices in flight at once. Enough to keep a fast link busy through the
 * per-ack round trip, few enough that the socket never buffers the whole file.
 */
export const FILE_UPLOAD_WINDOW = 8;

export type UploaderStatus =
  | "idle"
  | "connecting"
  | "online"
  | "offline"
  | "finalizing"
  | "finalized"
  | "failed";

export interface UploaderEvents {
  onStatus?: (status: UploaderStatus) => void;
  onPending?: (pending: number) => void;
  onFinalized?: (result: { bytes: number; chunkCount: number; gapSeqs: number[] }) => void;
  onFatal?: (message: string) => void;
}

export interface UploaderOptions extends UploaderEvents {
  recordingId: string;
  mimeType: string;
  wsUrl: string;
  /** Minted fresh on every connect: tokens are short-lived by design. */
  getToken: () => Promise<string>;
  /** Defaults to this recording's chunks in IndexedDB. */
  source?: ChunkSource;
  /**
   * Chunks sent and not yet acked. A capture trickles in 3s at a time and
   * leaves this unbounded; a file has all of its slices ready at once, and
   * sending them all would buffer the whole file inside the socket.
   */
  maxInFlight?: number;
}

export class ChunkUploader {
  private socket: WebSocket | null = null;
  private status: UploaderStatus = "idle";
  private attempt = 0;
  private stopped = false;
  private finalizeRequested = false;
  private finalizeSent = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private finalizeTimer: ReturnType<typeof setTimeout> | null = null;
  /** Seqs sent on the current socket and not yet acked. Reset on reconnect. */
  private inFlight = new Set<number>();
  private pumping = false;
  /** A wake-up arrived mid-pump; run it again rather than drop it. */
  private pumpAgain = false;
  private readonly source: ChunkSource;

  constructor(private readonly options: UploaderOptions) {
    this.source = options.source ?? indexedDbSource(options.recordingId);
  }

  get currentStatus(): UploaderStatus {
    return this.status;
  }

  start(): void {
    if (this.stopped) return;
    this.connect();
  }

  /** New chunk in the store - wake the drain loop. */
  notify(): void {
    void this.pump();
  }

  /**
   * The user pressed stop. Finalize is only sent once the queue is empty, so a
   * reconnect can still refill it first.
   */
  requestFinalize(): void {
    this.finalizeRequested = true;
    void this.pump();
  }

  /** Tears the socket down without finalizing - the session stays resumable. */
  dispose(): void {
    this.stopped = true;
    this.clearTimers();
    this.socket?.close(1000, "disposed");
    this.socket = null;
    this.setStatus("idle");
  }

  private setStatus(status: UploaderStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.options.onStatus?.(status);
  }

  private clearTimers(): void {
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    if (this.heartbeatTimer !== null) clearInterval(this.heartbeatTimer);
    if (this.finalizeTimer !== null) clearTimeout(this.finalizeTimer);
    this.reconnectTimer = this.heartbeatTimer = this.finalizeTimer = null;
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer !== null) return;
    // Full jitter: several tabs coming back from the same wifi drop must not
    // reconnect in lockstep.
    const ceiling = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** this.attempt);
    const delay = BACKOFF_MIN_MS + Math.random() * (ceiling - BACKOFF_MIN_MS);
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private connect(): void {
    if (this.stopped || this.socket !== null) return;
    this.setStatus("connecting");
    void (async () => {
      let token: string;
      try {
        token = await this.options.getToken();
      } catch (error) {
        // Convex being unreachable is the same class of problem as the socket
        // being down: keep the chunks and retry.
        this.setStatus("offline");
        this.scheduleReconnect();
        void error;
        return;
      }
      if (this.stopped) return;

      let socket: WebSocket;
      try {
        socket = new WebSocket(this.options.wsUrl);
      } catch {
        this.setStatus("offline");
        this.scheduleReconnect();
        return;
      }
      socket.binaryType = "arraybuffer";
      this.socket = socket;
      this.inFlight.clear();

      socket.onopen = () => {
        socket.send(
          JSON.stringify({
            type: "hello",
            recordingId: this.options.recordingId,
            token,
            mimeType: this.options.mimeType,
          }),
        );
      };
      socket.onmessage = (event) => this.handleMessage(event);
      socket.onerror = () => {
        // `onclose` always follows; reconnecting is handled there.
      };
      socket.onclose = () => {
        if (this.socket === socket) this.socket = null;
        this.inFlight.clear();
        if (this.stopped || this.status === "finalized") return;
        this.setStatus("offline");
        this.scheduleReconnect();
      };
    })();
  }

  private handleMessage(event: MessageEvent): void {
    if (typeof event.data !== "string") return;
    let message: ServerMessage;
    try {
      message = JSON.parse(event.data) as ServerMessage;
    } catch {
      return;
    }

    switch (message.type) {
      case "resume": {
        this.attempt = 0;
        this.source.resumed?.(message.lastAckedSeq, message.missingSeqs);
        this.setStatus("online");
        this.startHeartbeat();
        void this.pump();
        return;
      }
      case "ack": {
        this.inFlight.delete(message.seq);
        void (async () => {
          await this.source.acked(message.seq);
          await this.reportPending();
          void this.pump();
        })();
        return;
      }
      case "finalized": {
        this.clearTimers();
        this.setStatus("finalized");
        void patchSession(this.options.recordingId, { finalized: true });
        this.options.onFinalized?.({
          bytes: message.bytes,
          chunkCount: message.chunkCount,
          gapSeqs: message.gapSeqs,
        });
        this.stopped = true;
        return;
      }
      case "error": {
        if (
          message.code === "unknown_recording" ||
          message.code === "already_finalized"
        ) {
          // Nothing to retry: the server will never accept these bytes.
          this.stopped = true;
          this.clearTimers();
          this.setStatus(message.code === "already_finalized" ? "finalized" : "failed");
          this.options.onFatal?.(`${message.code}: ${message.message}`);
          return;
        }
        // bad_token / expired_token / storage_failed / internal are transient
        // from the client's point of view - a fresh token is minted on connect.
        this.setStatus("offline");
        return;
      }
    }
  }

  private startHeartbeat(): void {
    if (this.heartbeatTimer !== null) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(encodeFrame(FrameType.Heartbeat, 0, Date.now()));
      }
    }, HEARTBEAT_MS);
  }

  private async reportPending(): Promise<void> {
    if (this.options.onPending === undefined) return;
    this.options.onPending((await this.source.pendingSeqs()).length);
  }

  /**
   * Sends everything in the source that is not already in flight. Serialized
   * so two wake-ups cannot send the same seq twice; a wake-up that lands while
   * a pass is running queues one more pass, so an ack is never lost.
   */
  private async pump(): Promise<void> {
    if (this.pumping) {
      this.pumpAgain = true;
      return;
    }
    this.pumping = true;
    try {
      do {
        this.pumpAgain = false;
        await this.pumpOnce();
      } while (this.pumpAgain);
    } finally {
      this.pumping = false;
    }
  }

  private async pumpOnce(): Promise<void> {
    const socket = this.socket;
    if (socket === null || socket.readyState !== WebSocket.OPEN) return;
    if (this.status !== "online" && this.status !== "finalizing") return;

    const limit = this.options.maxInFlight ?? Number.POSITIVE_INFINITY;
    const seqs = await this.source.pendingSeqs();
    for (const seq of seqs) {
      if (this.inFlight.size >= limit) break;
      if (this.inFlight.has(seq)) continue;
      const chunk = await this.source.read(seq);
      if (chunk === null) continue;
      if (socket.readyState !== WebSocket.OPEN) return;
      socket.send(encodeFrame(FrameType.Chunk, seq, chunk.clientTs, chunk.payload));
      this.inFlight.add(seq);
    }
    this.options.onPending?.(seqs.length);

    if (this.finalizeRequested && !this.finalizeSent && seqs.length === 0) {
      this.sendFinalize(socket);
    } else if (this.finalizeRequested && !this.finalizeSent) {
      this.armFinalizeFallback();
    }
  }

  private sendFinalize(socket: WebSocket): void {
    this.finalizeSent = true;
    this.setStatus("finalizing");
    socket.send(encodeFrame(FrameType.Finalize, 0, Date.now()));
  }

  /**
   * If acks stop arriving after a stop - the socket died mid-drain - the pump
   * is re-run once the grace period passes so finalize is not lost forever.
   */
  private armFinalizeFallback(): void {
    if (this.finalizeTimer !== null) return;
    this.finalizeTimer = setTimeout(() => {
      this.finalizeTimer = null;
      void this.pump();
    }, FINALIZE_GRACE_MS);
  }
}

/** Rebuilds an uploader for a session found in IndexedDB after a crash. */
export function uploaderForSession(
  session: RecorderSession,
  wsUrl: string,
  getToken: () => Promise<string>,
  events: UploaderEvents = {},
): ChunkUploader {
  return new ChunkUploader({
    recordingId: session.recordingId,
    mimeType: session.mimeType,
    wsUrl,
    getToken,
    ...events,
  });
}
